import React, { useRef } from 'react';
import { useIsMutating } from 'react-query';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import {
  AssetToUpsert,
  OTHER_ASSETS_OPTIONS,
  UPSERT_ASSET_FORM_ID,
  UPSERT_ASSET_MK,
} from './UpsertAssetModal.meta';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { ASSET_NAME_MAP, OtherAssetType } from '../../OtherAssets.meta';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { AssetToDelete } from '../DeleteAssetModal/DeleteAssetModal.meta';

export interface Props {
  asset: AssetToUpsert;
  onAssetTypeChange(assetType: OtherAssetType): void;
  onClose(): void;
  assetToDelete: AssetToDelete | null;
  setAssetToDelete(assetToDelete: AssetToDelete | null): void;
  children: React.ReactNode;
}

export const UpsertAssetModal = ({
  asset,
  onAssetTypeChange,
  onClose,
  assetToDelete,
  setAssetToDelete,
  children,
}: Props) => {
  const isUpsertingAsset = useIsMutating([UPSERT_ASSET_MK]) > 0;

  const initialAssetType = useRef(asset.OtherAssetType).current;

  return (
    <Modal
      isOpen
      isHidden={assetToDelete !== null}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'other-assets--upsert-asset--modal-header' }}
    >
      <ModalHeader as="h2" id="other-assets--upsert-asset--modal-header">
        {initialAssetType !== undefined
          ? `${asset.Id ? 'Edit' : 'Add'} ${ASSET_NAME_MAP[initialAssetType]}`
          : 'Add Other Assets'}
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody css="min-height: 30vh">
        {initialAssetType === undefined && (
          <Select
            label="Asset Type"
            placeholder="Select Asset Type"
            value={OTHER_ASSETS_OPTIONS.find(({ value }) => value === asset.OtherAssetType)}
            options={OTHER_ASSETS_OPTIONS}
            onChange={opt => {
              if (opt) {
                onAssetTypeChange(opt.value);
              }
            }}
            isPortal
            mb="16"
          />
        )}

        <>{children}</>
      </ModalBody>

      {asset.OtherAssetType !== undefined && (
        <ModalFooter>
          {asset.Id && (
            <Button
              variant="errorSecondary"
              onClick={() => {
                if (asset.Id) {
                  setAssetToDelete({ id: asset.Id });
                }
              }}
              mr="auto"
            >
              Delete
            </Button>
          )}

          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            form={UPSERT_ASSET_FORM_ID}
            isLoading={isUpsertingAsset}
            ml="8"
          >
            {asset.Id ? 'Save' : 'Add Asset'}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};
